// src/components/admin/inscripciones/InscripcionesManager.js
// Pantalla de Gestion de Inscripciones por temporada: inscribir, activar y dar de baja.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserPlus, CheckCircle2, UserMinus, RotateCcw, Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModal } from '../../../hooks/useModal';
import { FeaturedLayout } from '../../layouts';
import {
  Button, Card, CardContent, Checkbox, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../ui';
import Modal from '../../ui/Modal';
import { useTemporadaActiva } from '../pagos/useTemporadaActiva';
import {
  getFilasInscripcion,
  getParticipacionByJugador,
  inscribirJugador,
  inscribirJugadores,
  setEstatusInscripcion,
  setEstatusInscripciones,
} from '../../../lib/supabase';
import { particionarSeleccion, resumenNoActivados, motivoNadaQueActivar } from '../../../lib/inscripcionBulk';
import EstadoInscripcionBadge from './EstadoInscripcionBadge';
import ReglasChecklist from './ReglasChecklist';
import ParticipacionBadges from './ParticipacionBadges';
import TemporadaContextBar from './TemporadaContextBar';

const InscripcionesManager = () => {
  const { isAdmin } = usePermissions();
  const { modal, showConfirm, showSuccess, showError, closeModal } = useModal();
  const { temporadaId, setTemporadaId, temporadas } = useTemporadaActiva();
  const [searchParams] = useSearchParams();
  const temporadaParam = searchParams.get('temporada');

  const [filas, setFilas] = useState([]);
  const [participacion, setParticipacion] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [actingId, setActingId] = useState(null);

  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  // Default 'todos' (antes 'relevantes'): un club que arranca tiene el roster
  // cargado y casi nadie en grupos todavia -- RAIDERS TRC: 197 jugadores, 2 con
  // grupo. Con 'relevantes' verian 2 filas y la pantalla pareceria vacia justo
  // cuando mas la necesitan.
  const [ambitoFilter, setAmbitoFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [inscripcionFilter, setInscripcionFilter] = useState('todos');

  const temporada = useMemo(
    () => temporadas.find((t) => t.id === temporadaId) || null,
    [temporadas, temporadaId]
  );

  // Si llegamos desde Temporadas con ?temporada=<id>, preseleccionar esa.
  useEffect(() => {
    if (temporadaParam && temporadas.some((t) => t.id === temporadaParam)) {
      setTemporadaId(temporadaParam);
    }
  }, [temporadaParam, temporadas, setTemporadaId]);

  const cargar = useCallback(async () => {
    // Al recargar (incluido cambiar de temporada) la seleccion muere: arrastrarla
    // dejaria marcados jugadores de otra temporada.
    setSelectedIds(new Set());
    if (!temporada) { setFilas([]); setParticipacion({}); return; }
    setLoading(true);
    const [{ data, error }, { data: part }] = await Promise.all([
      getFilasInscripcion(temporada),
      getParticipacionByJugador({ excluirTemporadaId: temporada.id }),
    ]);
    if (error) {
      await showError('Error', 'No se pudieron cargar las inscripciones.');
      setFilas([]);
    } else {
      setFilas(data || []);
    }
    // El indicador es contexto, no el dato principal: si falla, la pantalla sigue
    // sirviendo (la columna sale vacia) en vez de tumbar las inscripciones.
    setParticipacion(part || {});
    setLoading(false);
  }, [temporada, showError]);

  useEffect(() => { cargar(); }, [cargar]);

  const filasVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas.filter((f) => {
      if (q && !(f.nombre || '').toLowerCase().includes(q)) return false;
      if (ambitoFilter === 'relevantes' && !(f.inscrito || f.gruposDelDeporte.length > 0)) return false;
      if (inscripcionFilter === 'inscritos' && !f.inscrito) return false;
      if (inscripcionFilter === 'sin_inscribir' && f.inscrito) return false;
      if (statusFilter !== 'todos' && f.estadoMostrado !== statusFilter) return false;
      return true;
    });
  }, [filas, busqueda, ambitoFilter, inscripcionFilter, statusFilter]);

  const hasActiveFilters =
    ambitoFilter !== 'todos' || statusFilter !== 'todos' || inscripcionFilter !== 'todos';

  // La seleccion se limita SIEMPRE a lo visible: si eliges "todos" y luego
  // filtras, actuar sobre filas que ya no ves seria una sorpresa desagradable.
  const seleccionadas = useMemo(
    () => filasVisibles.filter((f) => selectedIds.has(f.jugadorId)),
    [filasVisibles, selectedIds],
  );
  const todasVisiblesSeleccionadas =
    filasVisibles.length > 0 && seleccionadas.length === filasVisibles.length;

  const toggleUno = (jugadorId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jugadorId)) next.delete(jugadorId); else next.add(jugadorId);
      return next;
    });
  };
  const toggleTodas = () => {
    setSelectedIds(todasVisiblesSeleccionadas
      ? new Set()
      : new Set(filasVisibles.map((f) => f.jugadorId)));
  };

  const conActing = async (id, fn) => {
    setActingId(id);
    try { await fn(); } finally { setActingId(null); }
  };

  // La inscripcion NO guarda grupo: el grupo dice donde entrena y vive en
  // jugadores_grupos; el cobro por grupo lo resuelve en vivo (getJugadoresInscritos).
  const handleInscribir = (f) => conActing(f.jugadorId, async () => {
    const { error } = await inscribirJugador({ jugadorId: f.jugadorId, temporada });
    if (error) return showError('Error', 'No se pudo inscribir al jugador.');
    await cargar();
  });

  const handleActivar = (f) => conActing(f.jugadorId, async () => {
    const { error } = await setEstatusInscripcion(f.inscripcionId, 'activo');
    if (error) return showError('Error', 'No se pudo activar la inscripción.');
    await cargar();
  });

  const handleBaja = (f) => conActing(f.jugadorId, async () => {
    const ok = await showConfirm('Dar de baja', `¿Dar de baja a ${f.nombre} de esta temporada?`, 'Dar de baja');
    if (!ok) return;
    const { error } = await setEstatusInscripcion(f.inscripcionId, 'baja');
    if (error) return showError('Error', 'No se pudo dar de baja.');
    await cargar();
  });

  const handleReactivar = (f) => conActing(f.jugadorId, async () => {
    const { error } = await setEstatusInscripcion(f.inscripcionId, 'pendiente');
    if (error) return showError('Error', 'No se pudo reactivar.');
    await cargar();
  });

  // --- Acciones masivas (spec §4.6) -----------------------------------------
  // La seleccion viene mixta, asi que cada accion actua solo donde aplica y
  // reporta lo que quedo fuera (particionarSeleccion / resumenNoActivados).
  // Una sola consulta para toda la seleccion, no una por jugador: un club con 200
  // inscritos generaria 200 peticiones.
  const correrLote = async (fn, { n, titulo, hecho, extra = null }) => {
    setBulkBusy(true);
    try {
      const { error } = await fn();
      if (error) return showError('Error', 'No se pudo completar la acción.');
      await cargar();
      const partes = [`${n} ${hecho}.`];
      if (extra) partes.push(extra);
      await showSuccess(titulo, partes.join(' '));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleInscribirSeleccion = async () => {
    const { inscribibles } = particionarSeleccion(seleccionadas);
    if (!inscribibles.length) {
      return showSuccess('Nada que inscribir', 'Los seleccionados ya están inscritos en esta temporada.');
    }
    const ok = await showConfirm(
      'Inscribir seleccionados',
      `Se inscribirán ${inscribibles.length} jugador(es) en "Pendiente". ¿Continuar?`,
      'Inscribir',
    );
    if (!ok) return;
    await correrLote(
      () => inscribirJugadores({
        jugadores: inscribibles.map((f) => f.jugadorId),
        temporada,
      }),
      { n: inscribibles.length, titulo: 'Listo', hecho: 'inscrito(s)' },
    );
  };

  const handleActivarSeleccion = async () => {
    const { activables, noActivables } = particionarSeleccion(seleccionadas);
    const aviso = resumenNoActivados(noActivables);
    if (!activables.length) {
      return showSuccess('Nada que activar', motivoNadaQueActivar(seleccionadas));
    }
    const ok = await showConfirm(
      'Activar seleccionados',
      `Se activarán ${activables.length} inscripción(es).${aviso ? ` ${aviso}` : ''} ¿Continuar?`,
      'Activar',
    );
    if (!ok) return;
    await correrLote(
      () => setEstatusInscripciones(activables.map((f) => f.inscripcionId), 'activo'),
      { n: activables.length, titulo: 'Listo', hecho: 'activada(s)', extra: aviso },
    );
  };

  const handleBajaSeleccion = async () => {
    const { bajables } = particionarSeleccion(seleccionadas);
    if (!bajables.length) {
      return showSuccess('Nada que dar de baja', 'Los seleccionados no están inscritos en esta temporada, o ya están dados de baja.');
    }
    const ok = await showConfirm(
      'Dar de baja',
      `¿Dar de baja a ${bajables.length} jugador(es) de esta temporada?`,
      'Dar de baja',
    );
    if (!ok) return;
    await correrLote(
      () => setEstatusInscripciones(bajables.map((f) => f.inscripcionId), 'baja'),
      { n: bajables.length, titulo: 'Listo', hecho: 'dado(s) de baja' },
    );
  };

  const acciones = (f) => {
    const busy = actingId === f.jugadorId;
    if (!f.inscrito) {
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => handleInscribir(f)}>
          <UserPlus className="h-4 w-4 mr-1" />{busy ? 'Inscribiendo...' : 'Inscribir'}
        </Button>
      );
    }
    if (f.estatusGuardado === 'baja') {
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => handleReactivar(f)}>
          <RotateCcw className="h-4 w-4 mr-1" />Reactivar
        </Button>
      );
    }
    return (
      <div className="flex justify-end gap-2">
        {f.estatusGuardado === 'pendiente' && (
          <Button
            size="sm"
            disabled={busy || !f.puedeActivar}
            title={f.puedeActivar ? '' : 'Falta cumplir reglas (CURP / consentimiento)'}
            onClick={() => handleActivar(f)}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />Activar
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={busy} onClick={() => handleBaja(f)}>
          <UserMinus className="h-4 w-4 mr-1" />Dar de baja
        </Button>
      </div>
    );
  };

  if (!isAdmin) {
    return (
      <FeaturedLayout title="Inscripciones" subtitle="Gestiona quién participa en cada temporada."
      refreshAction={cargar}>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">No tienes permisos para acceder a esta página</p>
        </div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout
      title="Inscripciones"
      subtitle="Gestiona quién participa en cada temporada."
      refreshAction={cargar}
    >
      {/* Toolbar */}
      {temporadas.length > 0 && (
        <div className="mb-4">
          <TemporadaContextBar
            temporada={temporada}
            temporadas={temporadas}
            value={temporadaId}
            onChange={setTemporadaId}
          />
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar jugador..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              <span className="font-medium text-foreground">{filasVisibles.length}</span> jugadores
            </span>
            <Button
              variant="outline"
              size="default"
              onClick={() => setShowFilters(!showFilters)}
              className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
            >
              <Filter className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Mostrar</Label>
              <Select value={ambitoFilter} onValueChange={setAmbitoFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los jugadores</SelectItem>
                  <SelectItem value="relevantes">Solo los del deporte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Inscripción</Label>
              <Select value={inscripcionFilter} onValueChange={setInscripcionFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="inscritos">Inscritos</SelectItem>
                  <SelectItem value="sin_inscribir">Sin inscribir</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Estado</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="no_inscrito">Sin inscribir</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="revision">Revisión</SelectItem>
                  <SelectItem value="baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => { setAmbitoFilter('todos'); setStatusFilter('todos'); setInscripcionFilter('todos'); }}
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {!temporada ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No hay temporadas. Crea una en Administración → Temporadas.
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
            <p className="text-muted-foreground">Cargando inscripciones...</p>
          </div>
        </div>
      ) : filasVisibles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No hay jugadores para mostrar con estos filtros.
          </CardContent>
        </Card>
      ) : (
        <Card>
          {seleccionadas.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-4 py-3">
              <span className="text-sm font-medium">
                {seleccionadas.length} seleccionado{seleccionadas.length === 1 ? '' : 's'}
              </span>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" disabled={bulkBusy} onClick={handleInscribirSeleccion}>
                  <UserPlus className="h-4 w-4 mr-1" />Inscribir
                </Button>
                <Button size="sm" variant="outline" disabled={bulkBusy} onClick={handleActivarSeleccion}>
                  <CheckCircle2 className="h-4 w-4 mr-1" />Activar
                </Button>
                {/* Es la unica que quita acceso, y sobre una seleccion multiple: se marca aparte */}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={bulkBusy}
                  onClick={handleBajaSeleccion}
                  className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                >
                  <UserMinus className="h-4 w-4 mr-1" />Dar de baja
                </Button>
                <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={todasVisiblesSeleccionadas}
                      onCheckedChange={toggleTodas}
                      aria-label="Seleccionar todos los jugadores visibles"
                    />
                  </TableHead>
                  <TableHead>Jugador</TableHead>
                  <TableHead className="hidden md:table-cell">Grupo(s)</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="hidden sm:table-cell">Reglas</TableHead>
                  <TableHead className="hidden lg:table-cell">Participación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filasVisibles.map((f) => (
                  <TableRow key={f.jugadorId} data-state={selectedIds.has(f.jugadorId) ? 'selected' : undefined}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(f.jugadorId)}
                        onCheckedChange={() => toggleUno(f.jugadorId)}
                        aria-label={`Seleccionar a ${f.nombre}`}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{f.nombre}</span>
                      {f.numero != null && <span className="text-muted-foreground"> · #{f.numero}</span>}
                      {/* Grupo(s) se oculta en movil; el dato baja aqui */}
                      <span className="block text-xs text-muted-foreground md:hidden">
                        {f.gruposDelDeporte.length
                          ? f.gruposDelDeporte.map((g) => g.nombre).join(', ')
                          : '— sin grupo'}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {f.gruposDelDeporte.length
                        ? f.gruposDelDeporte.map((g) => g.nombre).join(', ')
                        : '— sin grupo'}
                    </TableCell>
                    <TableCell><EstadoInscripcionBadge estado={f.estadoMostrado} /></TableCell>
                    <TableCell className="hidden sm:table-cell"><ReglasChecklist reglas={f.reglas} /></TableCell>
                    <TableCell className="hidden lg:table-cell"><ParticipacionBadges participacion={participacion[f.jugadorId]} /></TableCell>
                    <TableCell className="text-right">{acciones(f)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        showCancel={modal.showCancel}
      />
    </FeaturedLayout>
  );
};

export default InscripcionesManager;
